import { ScaleElement } from '@scale/base-element';
import { Events } from '@scale/events';

class ScaleCollectionGrid extends ScaleElement {
  static observeRefs = true;
  #controller;
  #scrollObserver;
  #loadingMore = false;

  setup() {
    this.onDocument(Events.FILTERS_UPDATED, (e) => this.#handleFilterUpdate(e));

    if (this.dataset.paginationType === 'infinite') {
      this.#observeSentinel();
    }
  }

  teardown() {
    this.#scrollObserver?.disconnect();
  }

  updatedCallback() {
    this.invalidateRefs();
    if (this.dataset.paginationType === 'infinite') {
      this.#observeSentinel();
    }
  }

  // --- Filter/sort update ---

  async #handleFilterUpdate(event) {
    this.#setLoading(true);
    this.#controller?.abort();
    this.#controller = new AbortController();

    try {
      const html = await this.#fetchPageHTML(event.url, this.#controller.signal);
      if (!this.#replaceFragment(html, 'layout')) this.#setLoading(false);
      requestAnimationFrame(() => this.#scrollToGrid());
      this.#announceCount();
    } catch (err) {
      if (err?.name !== 'AbortError') {
        this.#setLoading(false);
      }
    }
  }

  // --- Pagination ---

  handlePageClick(event) {
    event.preventDefault();
    const url = event.target.closest('a')?.href;
    if (!url) return;

    history.pushState({}, '', url);
    this.#renderPage(url);
  }

  async loadMore(event) {
    if (this.#loadingMore) return;
    const btn = event.target.closest('button');
    const url = btn?.dataset.nextUrl;
    if (!url) return;

    this.#loadingMore = true;
    btn.setAttribute('aria-busy', 'true');

    try {
      const html = await this.#fetchPageHTML(url);
      this.#appendProducts(html);
      history.replaceState({}, '', url);
    } catch (err) {
      if (err?.name !== 'AbortError') {
        btn.removeAttribute('aria-busy');
      }
    }

    this.#loadingMore = false;
  }

  // --- Infinite scroll ---

  #observeSentinel() {
    this.#scrollObserver?.disconnect();

    const sentinel = this.ref('sentinel');
    if (!sentinel) return;

    this.#scrollObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.#loadNextPage();
        }
      },
      { rootMargin: '200px' }
    );
    this.#scrollObserver.observe(sentinel);
  }

  async #loadNextPage() {
    if (this.#loadingMore) return;
    this.#loadingMore = true;
    this.#scrollObserver?.disconnect();

    const pagination = this.ref('pagination');
    const btn = pagination?.querySelector('[data-next-url]');
    const url = btn?.dataset.nextUrl;
    if (!url) {
      this.#loadingMore = false;
      return;
    }

    try {
      const html = await this.#fetchPageHTML(url);
      this.#appendProducts(html);
      history.replaceState({}, '', url);
    } catch { /* silent — will retry on next scroll */ }

    this.#loadingMore = false;
  }

  // --- Rendering helpers ---

  async #renderPage(url) {
    this.#setLoading(true);
    this.#controller?.abort();
    this.#controller = new AbortController();

    try {
      const html = await this.#fetchPageHTML(url, this.#controller.signal);
      if (!this.#replaceFragment(html, 'products')) this.#setLoading(false);
      requestAnimationFrame(() => this.#scrollToGrid());
    } catch (err) {
      if (err?.name !== 'AbortError') {
        this.#setLoading(false);
      }
    }
  }

  async #fetchPageHTML(url, signal = this.signal) {
    const sectionId = this.dataset.sectionId;
    const fetchUrl = new URL(url, window.location.origin);
    const currentUrl = new URL(window.location.href);

    if (currentUrl.searchParams.has('preview_theme_id') && !fetchUrl.searchParams.has('preview_theme_id')) {
      fetchUrl.searchParams.set('preview_theme_id', currentUrl.searchParams.get('preview_theme_id'));
    }

    fetchUrl.searchParams.set('section_id', sectionId);

    const response = await fetch(fetchUrl, { signal });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.text();
  }

  #replaceFragment(html, ref) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const incoming = doc.querySelector(`[data-ref="${ref}"]`);
    const current = this.ref(ref);
    if (!incoming || !current) return false;

    current.replaceWith(document.importNode(incoming, true));
    this.invalidateRefs();

    if (this.dataset.paginationType === 'infinite') {
      this.#observeSentinel();
    }

    return true;
  }

  #appendProducts(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newGrid = doc.querySelector('[data-ref="grid"]');
    const grid = this.ref('grid');
    if (!newGrid || !grid) return;

    for (const item of [...newGrid.children]) {
      grid.appendChild(document.importNode(item, true));
    }

    const newPagination = doc.querySelector('[data-ref="pagination"]');
    const pagination = this.ref('pagination');
    if (pagination && newPagination) {
      pagination.replaceWith(document.importNode(newPagination, true));
    } else if (pagination && !newPagination) {
      pagination.remove();
    }

    this.invalidateRefs();

    const newCount = doc.querySelector('[data-ref="product-count"]');
    const count = this.ref('product-count');
    if (count && newCount) count.textContent = newCount.textContent;

    // Re-observe sentinel for continuous infinite scroll
    if (this.dataset.paginationType === 'infinite') {
      this.#observeSentinel();
    }
  }

  // --- UI helpers ---

  #setLoading(loading) {
    this.ref('grid')?.toggleAttribute('data-loading', loading);
  }

  #scrollToGrid() {
    const toolbar = this.ref('toolbar');
    if (toolbar) {
      toolbar.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  #announceCount() {
    const count = this.ref('product-count');
    if (count) {
      this.announce(count.textContent, 'polite');
    }
  }
}

if (!customElements.get('scale-collection-grid')) {
  customElements.define('scale-collection-grid', ScaleCollectionGrid);
}
